import {
  Archive, BookOpen, CheckCircle, ChevronRight, Cpu, FileText, Image,
  LayoutGrid, MessageSquare, Monitor, MoreHorizontal, Plus, Save, Search,
  Send, Sparkles, User, Workflow,
} from 'lucide-react';

function RailItem({ icon: Icon, label, active }) {
  return (
    <div className={`group flex h-11 w-11 items-center justify-center rounded-2xl ${active ? 'bg-[#eee9de] text-[#6f6196]' : 'text-stone-400 hover:bg-[#f3eee5] hover:text-stone-700'}`} title={label}>
      <Icon size={17} />
    </div>
  );
}

function Step({ label, active, done }) {
  return (
    <div className={`flex items-center gap-2 rounded-full px-3 py-1.5 text-[12px] ${active ? 'bg-[#eee9de] text-[#6f6196]' : done ? 'text-stone-500' : 'text-stone-300'}`}>
      {done ? <CheckCircle size={13} /> : <span className={`h-2 w-2 rounded-full ${active ? 'bg-[#8c7db0]' : 'bg-stone-300'}`} />}
      {label}
    </div>
  );
}

export default function PrototypeWritingPage() {
  return (
    <div className="flex h-screen overflow-hidden bg-[#f4f1e9] text-stone-900">
      <aside className="flex w-[64px] shrink-0 flex-col items-center border-r border-[#ded6c8] bg-[#fbfaf6] py-3">
        <div className="mb-5 flex h-9 w-9 items-center justify-center rounded-2xl bg-white shadow-sm">
          <img src="/logo.png" alt="小影" className="h-7 w-7 object-contain" />
        </div>
        <nav className="flex flex-1 flex-col items-center gap-2">
          <RailItem icon={Sparkles} label="创意台" />
          <RailItem icon={Cpu} label="写剧本" active />
          <RailItem icon={BookOpen} label="剧本管理" />
          <RailItem icon={Workflow} label="无限画布" />
          <RailItem icon={Archive} label="资产管理" />
        </nav>
        <div className="flex flex-col items-center gap-2">
          <RailItem icon={User} label="个人中心" />
          <RailItem icon={Monitor} label="跟随系统" />
        </div>
      </aside>

      <main className="flex min-w-0 flex-1">
        <section className="flex w-[390px] shrink-0 flex-col border-r border-[#ded6c8] bg-[#fbfaf6]">
          <header className="border-b border-[#e4dccf] px-6 py-5">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-[#eee9de] text-[#6f6196]"><Cpu size={18} /></div>
              <div>
                <h1 className="text-[20px] font-semibold">写剧本</h1>
                <p className="mt-1 text-[12px] text-stone-400">AI 陪你从故事大纲写到单集正文</p>
              </div>
            </div>
            <button className="mt-5 flex w-full items-center justify-center gap-2 rounded-2xl bg-[#8c7db0] px-4 py-3 text-[13px] font-medium text-white shadow-lg shadow-[#8c7db0]/20">
              <Plus size={15} /> 新建短剧项目
            </button>
          </header>

          <div className="border-b border-[#e4dccf] p-4">
            <div className="flex items-center gap-2 rounded-2xl border border-[#ded6c8] bg-white px-3 py-2.5 text-[13px] text-stone-400">
              <Search size={14} /> 搜索项目 / 集数 / 人物
            </div>
          </div>

          <div className="min-h-0 flex-1 space-y-3 overflow-auto p-4">
            <button className="w-full rounded-2xl border border-[#c9bedc] bg-[#eee9de] p-4 text-left shadow-sm">
              <div className="flex items-start gap-3">
                <div className="mt-0.5 flex h-10 w-10 items-center justify-center rounded-xl bg-[#8c7db0] text-white"><FileText size={17} /></div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[14px] font-semibold text-stone-900">你好啊</p>
                  <p className="mt-1 text-[12px] text-stone-500">古风穿越 · 暂无集数</p>
                  <div className="mt-3 flex items-center gap-2 text-[11px] text-stone-400">
                    <span>创意阶段</span><span>0 字</span><span>今天编辑</span>
                  </div>
                </div>
                <ChevronRight size={15} className="text-stone-400" />
              </div>
            </button>
            {['仙路浮生梦', '三秒钟的垃圾', '她从未来回来'].map((name, i) => (
              <button key={name} className="w-full rounded-2xl border border-[#ded6c8] bg-white/75 p-4 text-left hover:bg-white">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#f0ebe2] text-stone-500"><BookOpen size={16} /></div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[14px] font-medium text-stone-800">{name}</p>
                    <p className="mt-1 text-[12px] text-stone-400">{i === 0 ? '13 集 · 剧本正文' : i === 1 ? '5 集 · 大纲阶段' : '概念设定'}</p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </section>

        <section className="flex min-w-0 flex-1 flex-col bg-[#f7f3eb]">
          <header className="flex h-[74px] items-center gap-4 border-b border-[#ded6c8] bg-[#fbfaf6]/90 px-6 backdrop-blur">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <h2 className="truncate text-[18px] font-semibold">你好啊</h2>
                <span className="rounded-full bg-[#eee9de] px-2.5 py-1 text-[11px] text-[#6f6196]">古风穿越</span>
              </div>
              <p className="mt-1 text-[12px] text-stone-400">当前阶段：创意 → 故事大纲 → 分集大纲 → 正文</p>
            </div>
            <button className="rounded-xl border border-[#ded6c8] bg-white px-3 py-2 text-[12px] text-stone-600"><Save size={14} className="mr-1 inline" />保存</button>
            <button className="rounded-xl border border-[#ded6c8] bg-white px-3 py-2 text-[12px] text-stone-600"><MoreHorizontal size={14} /></button>
          </header>

          <div className="flex items-center gap-2 border-b border-[#e4dccf] bg-[#fbfaf6]/70 px-6 py-3">
            <Step label="创意" done />
            <Step label="故事大纲" active />
            <Step label="分集大纲" />
            <Step label="写正文" />
            <Step label="完善" />
          </div>

          <div className="grid min-h-0 flex-1 grid-cols-[430px_1fr_52px]">
            <section className="flex min-h-0 flex-col border-r border-[#ded6c8] bg-[#fbfaf6]">
              <div className="border-b border-[#e4dccf] px-5 py-4">
                <p className="text-[13px] font-semibold text-stone-800">写作助手</p>
                <p className="mt-1 text-[12px] text-stone-400">像对话一样推进剧本，不再堆复杂表单</p>
              </div>
              <div className="min-h-0 flex-1 space-y-4 overflow-auto p-5">
                <div className="rounded-2xl border border-[#ded6c8] bg-white p-4 text-[13px] leading-6 text-stone-700">
                  <span className="mb-2 inline-flex rounded-full bg-[#eee9de] px-2 py-1 text-[11px] text-[#6f6196]">AI 建议</span>
                  <p>现在创意还差「世界观、核心矛盾、主角」。先不要急着写正文，先把故事大纲定下来。</p>
                </div>
                <div className="rounded-2xl border border-[#ded6c8] bg-[#fffdf8] p-4 text-[13px] leading-6 text-stone-700">
                  <p className="font-medium text-stone-900">你可以这样补：</p>
                  <p className="mt-2">无限流世界，包含多个悬疑副本。主角起点是普通人，但拥有识破规则漏洞的能力。</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {['设定世界观', '写核心矛盾', '创建主角', '生成故事大纲'].map(item => <button key={item} className="rounded-full border border-[#ded6c8] bg-white px-3 py-2 text-[12px] text-stone-600">{item}</button>)}
                </div>
              </div>
              <div className="border-t border-[#e4dccf] p-4">
                <div className="rounded-2xl border border-[#ded6c8] bg-white p-3">
                  <textarea className="h-20 w-full resize-none bg-transparent text-[13px] outline-none placeholder:text-stone-400" placeholder="告诉写作助手你想做什么，比如：帮我把这个无限流故事扩成故事大纲…" />
                  <div className="flex items-center pt-2">
                    <span className="text-[11px] text-stone-400">Enter 换行 · Ctrl/⌘ Enter 发送</span>
                    <button className="ml-auto flex h-9 w-9 items-center justify-center rounded-xl bg-[#8c7db0] text-white"><Send size={15} /></button>
                  </div>
                </div>
              </div>
            </section>

            <section className="min-h-0 overflow-auto bg-[#f7f3eb] p-8">
              <div className="mx-auto max-w-[720px] rounded-[28px] border border-[#ded6c8] bg-[#fffdf8] p-8 shadow-[0_20px_60px_rgba(76,64,44,0.08)]">
                <div className="mb-6 flex items-center justify-between border-b border-[#eee6d8] pb-4">
                  <div>
                    <p className="text-[12px] text-stone-400">故事大纲</p>
                    <h2 className="mt-1 text-[22px] font-semibold">无限流悬疑短剧</h2>
                  </div>
                  <span className="rounded-full bg-[#eee9de] px-3 py-1.5 text-[12px] text-[#6f6196]">草稿</span>
                </div>
                <div className="space-y-6 text-[15px] leading-8 text-stone-800">
                  <section>
                    <h3 className="mb-2 text-[14px] font-semibold text-stone-900">一句话故事</h3>
                    <p>普通玩家被卷入无限流副本，靠识破规则漏洞一次次活下来，却发现每个副本都在指向自己失踪的亲人。</p>
                  </section>
                  <section>
                    <h3 className="mb-2 text-[14px] font-semibold text-stone-900">核心矛盾</h3>
                    <p>系统要求玩家遵守规则才能活命，但真正的生路往往藏在规则的漏洞里。主角每次破局，都会换来更高代价。</p>
                  </section>
                  <section>
                    <h3 className="mb-2 text-[14px] font-semibold text-stone-900">下一步</h3>
                    <p className="text-stone-500">补全主角动机、反派规则系统、前三集强钩子后，再进入分集大纲。</p>
                  </section>
                </div>
              </div>
            </section>

            <aside className="flex flex-col items-center border-l border-[#ded6c8] bg-[#fbfaf6] py-4">
              {[LayoutGrid, FileText, User, Image, MessageSquare].map((Icon, index) => (
                <button key={index} className={`mb-2 flex h-9 w-9 items-center justify-center rounded-xl ${index === 1 ? 'bg-[#eee9de] text-[#6f6196]' : 'text-stone-400 hover:bg-[#f3eee5]'}`}>
                  <Icon size={15} />
                </button>
              ))}
              <div className="mt-auto flex -rotate-90 whitespace-nowrap text-[11px] text-stone-400">项目资料</div>
            </aside>
          </div>
        </section>
      </main>
    </div>
  );
}
